import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, merge } from 'rxjs';
import { debounceTime, delay, takeUntil, tap } from 'rxjs/operators';
import { AllInternalWorkersGridDataSource } from './internal-workers-list.datasource';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { InternalWorkersListFiltersComponent } from './internal-workers-list-filters/internal-workers-list-filters.component';
import { buildFilterArray } from 'src/app/common/utils';
import { Filter } from 'src/app/models/common/filter';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Permission } from 'src/app/common/enums';

@Component({
  selector: 'app-internal-workers-list',
  templateUrl: './internal-workers-list.component.html',
  styleUrls: ['./internal-workers-list.component.scss'],
  providers: [AllInternalWorkersGridDataSource],
  standalone: false
})
export class InternalWorkersListComponent implements OnInit, AfterViewInit {
  private spinner = inject(NgxSpinnerService);
  private translateService = inject(TranslateService);
  private formBuilder = inject(UntypedFormBuilder);
  private authService = inject(AuthService);
  public dataSource = inject(AllInternalWorkersGridDataSource, { self: true });

  displayedColumns: string[] = [];
  filtersFormGroup: UntypedFormGroup;
  areFiltersExpanded: boolean = false;

  private filters: Filter[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private readonly defaultPage: number = 1;
  private readonly defaultPageSize: number = 10;
  private readonly defaultSortColumn: string = 'WorkerId';
  private readonly defaultSortDirection: string = 'desc';

  private readonly unsubscribe$ = new Subject<void>();


  constructor() {
    this.displayedColumns = ['WorkerId', 'LastName', 'Email', 'PhoneNumber', 'EmployerName']
    if (this.areRolesColumnsVisible()) {
      this.displayedColumns.push('IsWorker');
      this.displayedColumns.push('Roles');
      this.displayedColumns.push('Actions');
    }
    else {
      this.displayedColumns.push('Actions');
    }
  }

  ngOnInit(): void {
    this.buildFormGroup();
    this.filters = buildFilterArray(this.filtersFormGroup, InternalWorkersListFiltersComponent.operatorsMap);
    this.dataSource.fetchInternalWorkers(this.defaultPage, this.defaultPageSize, this.defaultSortColumn, this.defaultSortDirection, this.filters);
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(
        tap(() => this.dataSource.fetchInternalWorkers(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active, this.sort.direction, this.filters)),
      )
      .subscribe();

    this.dataSource.loading$
      .pipe(
        delay(0),
        tap((x) => {
          x ? this.spinner.show() : this.spinner.hide();
        }),
      )
      .subscribe();
  }

  filterData(filters: Filter[]) {
    this.filters = filters;
    this.dataSource.fetchInternalWorkers(
      this.defaultPage,
      this.paginator?.pageSize ?? this.defaultPageSize,
      this.sort?.active ?? this.defaultSortColumn,
      this.sort?.direction ?? this.defaultSortDirection,
      this.filters,
    );
  }

  toggleFiltersPanel = () => (this.areFiltersExpanded = !this.areFiltersExpanded);

  resetFilters = (): void => this.filtersFormGroup.reset();

  areRolesColumnsVisible() {
    return this.authService.hasPermission(Permission.InternalWorkerRolesVisibility);
  }

  private buildFormGroup(): void {
    this.filtersFormGroup = this.formBuilder.group({
      firstName: [null],
      lastName: [null],
      employerName: [null],
      employer: [null],
      id: [null],
      email: [null],
      phoneNumber: [null]
    });

    this.storeFiltersValueInSessionStorage();
  }

  private storeFiltersValueInSessionStorage() {
    const internalWorkersFilters = 'internal-workers-filters';

    const filters = JSON.parse(sessionStorage.getItem(internalWorkersFilters));

    if (filters && Object.values(filters).some((v) => !!v)) {
      this.filtersFormGroup.patchValue(filters);
      this.areFiltersExpanded = true;
    }

    this.filtersFormGroup.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(1000)).subscribe(() => {
      if (this.filtersFormGroup.invalid) return;
      sessionStorage.setItem(internalWorkersFilters, JSON.stringify(this.filtersFormGroup.getRawValue()));
    });
  }
}
